/**
 * DocumentActions - PDF preview/download and email buttons for quotes and invoices
 * PROMPT 40: Financial Documents Unified View Layer
 * PROMPT 41: Financial Communications - Fixed button visibility
 * EF-PDF-DUAL-MODE: Added dual output modes (Summary + Detailed)
 * EVENTSFLO-QUOTES-PAGE-SUMMARY-DETAILED-PDF-AUTH-FIX-V1:
 *   Accepts authenticated callback handlers (onPreviewSummary / onPreviewDetailed /
 *   onDownloadSummary / onDownloadDetailed). When provided, those callbacks must
 *   perform the authenticated blob fetch + window.open/download. The legacy
 *   pdfUrlSummary / pdfUrlDetailed URL props remain only as fallbacks for callers
 *   that still rely on public/unauthenticated routes (none should in production).
 */
import { Button } from "../ui/button";
import { ArrowLeft, Printer, Download, ExternalLink, Mail, FileText, List } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";

export function DocumentActions({
  onBack,
  backLabel = "Back",
  pdfUrlSummary,
  pdfUrlDetailed,
  downloadFileName = "document.pdf",
  onPrint,
  showPrint = true,
  onSendEmail,
  showSendEmail = false,
  // EVENTSFLO-QUOTES-PAGE-SUMMARY-DETAILED-PDF-AUTH-FIX-V1
  onPreviewSummary,
  onPreviewDetailed,
  onDownloadSummary,
  onDownloadDetailed,
}) {
  const handleDownload = (url, mode) => {
    const link = document.createElement("a");
    link.href = url;
    // Add mode to filename: quote-QT-001-summary.pdf or quote-QT-001-detailed.pdf
    const baseName = downloadFileName.replace('.pdf', '');
    link.download = `${baseName}-${mode}.pdf`;
    link.click();
  };

  const handlePreview = (url) => {
    window.open(url, "_blank");
  };

  // EVENTSFLO-QUOTES-PAGE-SUMMARY-DETAILED-PDF-AUTH-FIX-V1: prefer authed callbacks.
  const runAuthedAction = async (fn, fallback, errorMsg) => {
    if (typeof fn === "function") {
      try {
        await fn();
      } catch (err) {
        toast.error(err?.message || errorMsg);
      }
      return;
    }
    if (fallback) fallback();
  };

  return (
    <div className="print:hidden flex items-center justify-between">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="text-muted-foreground hover:text-foreground"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {backLabel}
      </Button>

      <div className="flex gap-2">
        {/* EF-QUOTE-UX-MICRO-IMPROVEMENTS-V1: Renamed Preview to View PDF */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="border-border bg-surface-elevated text-foreground hover:bg-surface-elevated hover:text-foreground"
              data-testid="preview-pdf-dropdown"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View PDF
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Output Mode
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                runAuthedAction(
                  onPreviewSummary,
                  () => handlePreview(pdfUrlSummary),
                  "Failed to open Summary PDF",
                )
              }
              data-testid="preview-summary-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              View Summary PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                runAuthedAction(
                  onPreviewDetailed,
                  () => handlePreview(pdfUrlDetailed),
                  "Failed to open Detailed PDF",
                )
              }
              data-testid="preview-detailed-btn"
            >
              <List className="h-4 w-4 mr-2" />
              View Detailed PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Download Dropdown - Summary & Detailed */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="border-border bg-surface-elevated text-foreground hover:bg-surface-elevated hover:text-foreground"
              data-testid="download-pdf-dropdown"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Output Mode
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                runAuthedAction(
                  onDownloadSummary,
                  () => handleDownload(pdfUrlSummary, 'summary'),
                  "Failed to download Summary PDF",
                )
              }
              data-testid="download-summary-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Download Summary
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                runAuthedAction(
                  onDownloadDetailed,
                  () => handleDownload(pdfUrlDetailed, 'detailed'),
                  "Failed to download Detailed PDF",
                )
              }
              data-testid="download-detailed-btn"
            >
              <List className="h-4 w-4 mr-2" />
              Download Detailed
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Send Email - always visible when enabled */}
        {showSendEmail && (
          <Button
            variant="outline"
            onClick={onSendEmail}
            className="border-teal-600 bg-teal-900/50 text-teal-200 hover:bg-teal-800 hover:text-foreground"
            data-testid="send-email-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Send Email
          </Button>
        )}
        
        {/* Print - always visible */}
        {showPrint && (
          <Button
            onClick={onPrint || (() => window.print())}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="print-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        )}
      </div>
    </div>
  );
}

export default DocumentActions;
